import { hashFileName, uploadRoot, isReaddyCdnUrl, assetExtension } from "@/utils/media";
import { request, IS_OFFLINE } from "@/services/http";

/**
 * Runtime image host + background self-hosting.
 *
 * Demo/fallback artwork AND admin branding (logo, hero, gallery) are generated
 * or stored on a Readdy CDN. On the live server we want every one of those
 * images served from our own domain under /uploads/mock — but NOT at the cost of
 * a broken page if the files have not been downloaded yet.
 *
 * Two things happen here:
 *   1. Every CDN-backed <img> is transparently rewritten to its local copy
 *      (named by the deterministic URL hash, see src/utils/media.ts) and falls
 *      back to the original CDN URL the instant the local file 404s. So the page
 *      looks identical whether or not the mirror has finished.
 *   2. The CDN URLs the page actually needs are reported to the backend in the
 *      background (POST /public/mirror-images). Any that are missing are copied
 *      into /uploads/mock server-side — so the site heals onto its own domain by
 *      itself, without a manual sync. A real image uploaded to the same path is
 *      never touched.
 */

function localMockUrl(src: string): string {
  return `${uploadRoot()}/mock/${hashFileName(src, assetExtension(src))}`;
}

// ---- Background mirror reporting -----------------------------------------
const pending = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;

function flushMirror(): void {
  flushTimer = undefined;
  if (pending.size === 0) return;
  const urls = Array.from(pending).slice(0, 100);
  pending.clear();
  // Fire-and-forget: a failure here must never affect the visitor.
  request("/public/mirror-images", { method: "POST", body: { urls } }).catch(() => undefined);
}

/** Queues a CDN URL to be self-hosted by the backend (debounced, batched). */
function reportForMirror(url: string): void {
  if (IS_OFFLINE || pending.has(url)) return;
  pending.add(url);
  if (!flushTimer) flushTimer = setTimeout(flushMirror, 1500);
}

/** Swaps an <img> to its server copy (deterministic hash file name). */
function trySwap(img: HTMLImageElement): void {
  if (img.dataset.mockHost) return; // already handled
  const src = img.getAttribute("src") || "";
  if (!isReaddyCdnUrl(src)) return;
  reportForMirror(src);
  img.dataset.mockHost = "local";
  img.dataset.cdnSrc = src;
  img.setAttribute("src", localMockUrl(src));
}

/** Resource errors do not bubble, so this runs in the capture phase. */
function onResourceError(event: Event): void {
  const el = event.target as HTMLElement | null;
  if (!el || el.tagName !== "IMG") return;
  const img = el as HTMLImageElement;
  if (img.dataset.mockHost === "local" && img.dataset.cdnSrc) {
    const cdn = img.dataset.cdnSrc;
    img.dataset.mockHost = "cdn";
    img.removeAttribute("data-cdn-src");
    img.setAttribute("src", cdn);
  }
}

function scan(root: Document | HTMLElement): void {
  root.querySelectorAll?.("img").forEach((node) => trySwap(node as HTMLImageElement));
}

/** Installs the CDN → server rewrite + graceful fallback. Safe to call once. */
export function installMockImageHost(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", onResourceError, true);

  const start = () => {
    scan(document);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.target instanceof HTMLImageElement) {
          trySwap(mutation.target);
        }
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLImageElement) trySwap(node);
          else if (node instanceof HTMLElement) scan(node);
        });
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src"],
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}